import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IAddPermissionRelationResponse, IAddUserRoleRelationResponse, IPermissionDto, IPermissionType, IRoleDto, IUserContext, IUserDto, IEffectivePermissionCache, UserDto, PermissionStatusDto, IAddUserTenantRelationResponse } from 'src/app/models/user';
import { ConfigurationService, ForceBaseUrl } from 'src/app/services/configuration/configuration.service';
import { EndpointFactory } from '../endpoint-factory.service';
import { UserTenant } from 'src/app/models/tenant';

@Injectable({
    providedIn: 'root'
})
export class UserEndpoint extends EndpointFactory {

    getServiceBaseUserUrl(force?: ForceBaseUrl) {
        return this.getBaseUrl('users', 'WEBAPI') + 'users';
    }

    getServiceBaseRoleUrl(force?: ForceBaseUrl) {
        return this.getBaseUrl('roles', 'WEBAPI') + 'roles';
    }

    getServiceBasePermissionUrl(force?: ForceBaseUrl) {
        return this.getBaseUrl('permissions', 'WEBAPI') + 'permissions';
    }


    constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
        super(http, configurations, injector);
    }

    getUsersByRoles(roleNames: string[]): Promise<IUserDto[]> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/usersByRoles`;

        return this.http.post<IUserDto[]>(endpointUrl, roleNames)
            .pipe<IUserDto[]>(
                catchError(error => {
                    return this.handleError(error);
                })).toPromise();
    }

    getUserContext(userId: string): Observable<IUserContext> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${userId}/context`;

        return this.http.get<IUserContext>(endpointUrl)
            .pipe<IUserContext>(
                catchError(error => {
                    return this.handleError(error);
                }));
    }

    getUserNameById(userId: string): Promise<IUserDto> {

        const url = `${this.getServiceBaseUserUrl()}/${userId}`;

        return this.http.get<IUserDto>(url).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getUserByUserPrincipleName(upn: string): Promise<IUserDto> {

        const url = `${this.getServiceBaseUserUrl()}/upn/${upn}`;

        return this.http.get<IUserDto>(url).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getUsers(includeDisabled: boolean = false): Promise<UserDto[]> {

        let endpointUrl = `${this.getServiceBaseUserUrl()}?includeDisabled=${includeDisabled}`;

        return this.http.get<IUserDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            }),
            map(users => users.map(user => new UserDto(user)))).toPromise();
    }

    getAllRoles(): Promise<IRoleDto[]> {

        const endpointUrl = `${this.getServiceBaseRoleUrl()}`;

        return this.http.get<IRoleDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }


    getRoles(): Observable<IRoleDto[]> {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}`;

        return this.http.get<IRoleDto[]>(endpointUrl).pipe<IRoleDto[]>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getAllPermissions(): Promise<IPermissionDto[]> {

        const endpointUrl = `${this.getServiceBasePermissionUrl()}`;

        return this.http.get<IPermissionDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getUsersByRoleObjectId(roleObjectId: string): Promise<UserDto[]> {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}/${roleObjectId}/users`;

        return this.http.get<IUserDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            }),
            map(users => users.map(user => new UserDto(user)))).toPromise();
    }

    getPermissionsByRoleObjectId(objectId: string): Promise<IPermissionDto[]> {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}/${objectId}/permissions`;

        return this.http.get<IPermissionDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getUsersByPermissionId(permissionId: number): Promise<UserDto[]> {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}/${permissionId}/users`;

        return this.http.get<IUserDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            }),
            map(users => users.map(user => new UserDto(user)))).toPromise();
    }

    getRolesByPermissionId(permissionId: number): Promise<IRoleDto[]> {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}/${permissionId}/roles`;

        return this.http.get<IRoleDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getRolesByUserObjectId(objectId: string): Promise<IRoleDto[]> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${objectId}/roles`;

        return this.http.get<IRoleDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getPermissionsByUserObjectId(objectId: string): Promise<IPermissionDto[]> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${objectId}/permissions`;

        return this.http.get<IPermissionDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    addUserRoles(userObjectId: string, roles: string[]): Promise<IAddUserRoleRelationResponse> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${userObjectId}/roles`;

        return this.http.post<IAddUserRoleRelationResponse>(endpointUrl, roles).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    addRoleUsers(roleObjectId: string, users: string[]): Promise<IAddUserRoleRelationResponse> {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}/${roleObjectId}/users`;

        return this.http.post<IAddUserRoleRelationResponse>(endpointUrl, users).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    addUserPermissions(userObjectId: string, permissions: number[]): Promise<IAddPermissionRelationResponse> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${userObjectId}/permissions`;

        return this.http.post<IAddPermissionRelationResponse>(endpointUrl, permissions).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    addRolePermissions(roleObjectId: string, permissions: number[]): Promise<IAddPermissionRelationResponse> {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}/${roleObjectId}/permissions`;

        return this.http.post<IAddPermissionRelationResponse>(endpointUrl, permissions).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    addPermissionUsers(permissionId: number, userObjectIds: string[]) {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}/${permissionId}/users`;

        return this.http.post<IAddPermissionRelationResponse>(endpointUrl, userObjectIds).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }


    addPermissionRoles(permissionId: number, roleObjectIds: string[]) {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}/${permissionId}/roles`;

        return this.http.post<IAddPermissionRelationResponse>(endpointUrl, roleObjectIds).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getPermissionStatuses(roleObjectId: string, permissionNames: IPermissionType[]): Promise<PermissionStatusDto[]> {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}/role/${roleObjectId}/includeUser`;

        return this.http.post<PermissionStatusDto[]>(endpointUrl, permissionNames).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();


    }


    addPermissions(entities: IPermissionDto[]) :Promise<IPermissionDto[]> {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}`;

        return this.http.post<IPermissionDto[]>(endpointUrl, entities).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    updateUser(userObjectId: string, entity: IUserDto) {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${userObjectId}`;

        return this.http.put<IUserDto>(endpointUrl, entity).pipe(
            catchError(error => {
                return this.handleError(error);
            }), map(user => new UserDto(user))).toPromise();
    }

    async addRole(role: IRoleDto) {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}/add`;

        return this.http.post<IRoleDto>(endpointUrl, role).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }


    updateRole(roleObjectId: string, entity: IRoleDto) {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}/${roleObjectId}`;

        return this.http.put<IRoleDto>(endpointUrl, entity).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    updatePermission(permissionId: number, entity: IPermissionDto) {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}/${permissionId}`;

        return this.http.put<IPermissionDto>(endpointUrl, entity).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getUsersByPermissionName(permissionName: string, tenantId: string) {
        const endpointUrl = `${this.getServiceBasePermissionUrl()}/users/${tenantId}?identifier=${permissionName}`;

        return this.http.get<IUserDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            }),
            map(users => users.map(user => new UserDto(user)))).toPromise();
    }

    deleteUserPermissions(userPermissionIds: number[]) {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/permissions/delete`;

        return this.http.post<any>(endpointUrl, userPermissionIds).pipe<IEffectivePermissionCache[]>(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    deleteRoleUsers(roleObjectId: string, deletedUserObjectIds: string[]) {
        const endpointUrl = `${this.getServiceBaseRoleUrl()}/${roleObjectId}/users/delete`;

        return this.http.post<any>(endpointUrl, deletedUserObjectIds).pipe<IEffectivePermissionCache[]>(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }
    deleteUserRoles(userObjectId: string, roles: string[]) {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${userObjectId}/roles/delete`;

        return this.http.post<any[]>(endpointUrl, roles).pipe<IEffectivePermissionCache[]>(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getUserTenantsByUserObjectId(objectId: string): Promise<UserTenant[]> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${objectId}/tenants`;

        return this.http.get<UserTenant[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    addUserTenants(userObjectId: string, roles: string[]): Promise<IAddUserTenantRelationResponse> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${userObjectId}/tenants`;

        return this.http.post<IAddUserTenantRelationResponse>(endpointUrl, roles).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    deleteUserTenants(userObjectId: string, tenants: string[]) {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${userObjectId}/tenants`;

        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: tenants
        };

        return this.http.delete<any[]>(endpointUrl, httpOptions).pipe<IEffectivePermissionCache[]>(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    getUsersByTenantId(tenantId: string): Promise<UserDto[]> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${tenantId}/tenantUsers`;

        return this.http.get<IUserDto[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            }),
            map(users => users.map(user => new UserDto(user)))).toPromise();
    }

    addTenantUsers(tenantId: string, users: string[]): Promise<IAddUserRoleRelationResponse> {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${tenantId}/tenantUsers`;

        return this.http.post<IAddUserRoleRelationResponse>(endpointUrl, users).pipe(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

    deleteTenantUsers(tenantId: string, deletedUserObjectIds: string[]) {
        const endpointUrl = `${this.getServiceBaseUserUrl()}/${tenantId}/tenantUsers`;

        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: deletedUserObjectIds
        };

        return this.http.delete<any>(endpointUrl, httpOptions).pipe<IEffectivePermissionCache[]>(
            catchError(error => {
                return this.handleError(error);
            })).toPromise();
    }

}
